import type {
  BasePriceRuleFormValue,
  OPTION_TYPE,
} from 'src/interfaces/Template.interface'
import {
  getPriceRuleFieldDataId,
  getPriceRulePlatformFieldId,
  matchPriceRuleToField,
} from '../../../utils/priceTemplateFieldMatch'
import { generateDefaultPriceRules, type PriceFieldForRules } from '../hooks/usePriceModifierFields.previous'

type PriceHydrateField = PriceFieldForRules & {
  api_id?: string | number | null
  api_code?: string | null
}

type PriceHydrateRow = BasePriceRuleFormValue & {
  ruleId?: number | null
  destinationPlatformFieldDataId?: number | null
}

const deriveAdjustmentType = (val: number | undefined | null): 'INCREASE' | 'DECREASE' | 'NO_CHANGE' => {
  if (val == null || val === 0) return 'NO_CHANGE'
  return val > 0 ? 'INCREASE' : 'DECREASE'
}

export const unwrapPriceTemplatePayload = (payload: any): any => {
  if (!payload) return null
  if (Array.isArray(payload)) return payload
  if (payload.price_template && typeof payload.price_template === 'object') {
    return payload.price_template
  }
  return payload
}

export const resolvePriceTemplateId = (value: unknown): number | null => {
  if (value == null || value === '') return null
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null
  }
  if (typeof value === 'string') {
    const num = Number(value)
    return Number.isFinite(num) && num > 0 ? num : null
  }
  if (typeof value === 'object') {
    const record = value as { id?: unknown; price_template?: unknown }
    return resolvePriceTemplateId(record.id) ?? resolvePriceTemplateId(record.price_template)
  }
  return null
}

const hasPerFieldRuleMetadata = (rule: any): boolean => {
  if (!rule || typeof rule !== 'object') return false
  const destId = getPriceRuleFieldDataId(rule) ?? getPriceRulePlatformFieldId(rule)
  const sourceId =
    rule.source_platform_field_id ??
    rule.source_platform_field?.id ??
    rule.source_platform_field ??
    rule.mapped_field
  const apiName =
    rule.destination_platform_field?.api_name ||
    rule.destination_platform_field_api_name ||
    rule.api_name
  return (
    destId != null ||
    sourceId != null ||
    Boolean(apiName) ||
    Boolean(rule.start_date) ||
    Boolean(rule.end_date)
  )
}

const getNestedRuleList = (payload: any): any[] | null => {
  const root = unwrapPriceTemplatePayload(payload)
  if (!root || Array.isArray(root)) return Array.isArray(root) ? root : null

  const nested =
    root.price_rules ||
    root.template_fields ||
    root.price_template_data ||
    root.priceTemplateData ||
    root.price_template_mapping_data ||
    root.mapping_data

  return Array.isArray(nested) && nested.length > 0 ? nested : null
}

/** True when template-mapping already has per-row dest/source/date data. */
export const hasPerFieldPriceRules = (payload: any): boolean => {
  const nested = getNestedRuleList(payload)
  if (!nested) return false
  return nested.some(hasPerFieldRuleMetadata)
}

export const isUsablePriceHydratePayload = (payload: any): boolean => {
  const root = unwrapPriceTemplatePayload(payload)
  if (Array.isArray(root) && root.length > 0) return true
  if (!root || typeof root !== 'object') return false
  return Boolean(root.repricing) || hasPerFieldPriceRules(payload)
}

/** Extra GET only when recipe has an id but no per-row rule list or root repricing. */
export const needsPriceTemplateDetailFetch = (
  recipePrice: any,
  priceId: number | null
): boolean => {
  if (priceId == null) return false
  if (hasPerFieldPriceRules(recipePrice)) return false

  const root = unwrapPriceTemplatePayload(recipePrice)
  if (root && !Array.isArray(root) && root.repricing) return false

  return true
}

export const mergePriceHydratePayloads = (recipePrice: any, detailPayload: any): any => {
  const nested = unwrapPriceTemplatePayload(recipePrice)
  const detail = unwrapPriceTemplatePayload(detailPayload)
  if (!detail) return nested
  if (!nested) return detail
  if (Array.isArray(detail)) return detail
  if (!detail.repricing && nested.repricing && !Array.isArray(nested)) {
    return { ...detail, repricing: nested.repricing }
  }
  return detail
}

export const extractPriceTemplateRules = (payload: any): any[] => {
  const root = unwrapPriceTemplatePayload(payload)
  if (!root) return []
  if (Array.isArray(root)) return root

  const nested =
    root.price_rules ||
    root.template_fields ||
    root.price_template_data ||
    root.priceTemplateData ||
    root.price_template_mapping_data ||
    root.mapping_data

  if (Array.isArray(nested) && nested.length > 0) return nested
  if (root.repricing || root.id) return [root]
  return []
}

const resolveMappingOption = (rule: any): OPTION_TYPE => {
  const explicit = rule?.mapping_option
  if (explicit === 'DEFAULT' || explicit === 'NATIVE' || explicit === 'CUSTOM') {
    return explicit
  }
  const sourceId =
    rule?.source_platform_field_id ?? rule?.source_platform_field?.id ?? rule?.source_platform_field
  if (sourceId) {
    const fieldType = rule?.source_platform_field?.field_type || rule?.source_platform_field?.fieldType
    if (fieldType === 'NATIVE') return 'NATIVE'
    if (fieldType === 'CUSTOM') return 'CUSTOM'
    return 'NATIVE'
  }
  return 'DEFAULT'
}

const resolveMappedField = (rule: any): number | '' => {
  const mapped = rule?.mapped_field
  if (mapped && typeof mapped === 'object' && mapped.id != null) return Number(mapped.id)
  if (mapped != null && mapped !== '') return Number(mapped)
  const sourceId =
    rule?.source_platform_field_id ?? rule?.source_platform_field?.id ?? rule?.source_platform_field
  if (sourceId != null && sourceId !== '') return Number(sourceId)
  return ''
}

const applyRuleToRow = (row: PriceHydrateRow, rule: any): PriceHydrateRow => {
  const repricingValue = rule?.repricing?.value
  const defaultRaw = rule?.value_str ?? rule?.value ?? rule?.default_value
  const mappedField = resolveMappedField(rule)
  return {
    ...row,
    adjustmentMode: rule?.repricing?.type ?? row.adjustmentMode,
    adjustmentType: deriveAdjustmentType(repricingValue),
    adjustmentValue:
      repricingValue != null && Number(repricingValue) !== 0
        ? String(Math.abs(Number(repricingValue)))
        : row.adjustmentValue,
    destinationPlatformFieldId:
      getPriceRulePlatformFieldId(rule) != null
        ? Number(getPriceRulePlatformFieldId(rule))
        : row.destinationPlatformFieldId,
    mappingOption: resolveMappingOption(rule),
    mappedField: mappedField !== '' && Number.isFinite(mappedField) ? mappedField : row.mappedField,
    defaultValue:
      defaultRaw != null && defaultRaw !== '' ? String(defaultRaw) : row.defaultValue,
    isPriceDate: !!(rule?.start_date || rule?.end_date) || row.isPriceDate,
    startDate: rule?.start_date ?? row.startDate,
    endDate: rule?.end_date ?? row.endDate,
    ruleId: rule?.id ?? row.ruleId,
    destinationPlatformFieldDataId:
      getPriceRuleFieldDataId(rule) != null
        ? Number(getPriceRuleFieldDataId(rule))
        : row.destinationPlatformFieldDataId,
  }
}

export const mapPriceTemplateToQuickListForm = (
  payload: any,
  fields: PriceHydrateField[]
): {
  rules: PriceHydrateRow[]
  repricing: { type: string; value: number }
  adjustmentType: string
} => {
  const root = unwrapPriceTemplatePayload(payload)
  const rawRules = extractPriceTemplateRules(payload)
  const rootRepricing = Array.isArray(root) ? rawRules[0]?.repricing : root?.repricing
  const repricing = {
    type: rootRepricing?.type ?? 'FLAT',
    value: Number(rootRepricing?.value) || 0,
  }
  const adjustmentType = repricing.value < 0 ? 'decrease' : 'increase'

  if (!fields.length) {
    return { rules: [], repricing, adjustmentType }
  }

  const defaults = generateDefaultPriceRules(fields)
  const matched = defaults.map((row) => {
    const rule = rawRules.find((item) => {
      const field = matchPriceRuleToField(item, fields)
      return field?.id === row.templateFieldId
    })
    return rule ? applyRuleToRow(row, rule) : row
  })

  const anyMatched = matched.some((row, index) => row !== defaults[index])
  const fallbackRule = {
    ...(rawRules[0] ?? {}),
    repricing: rawRules[0]?.repricing ?? (Array.isArray(root) ? undefined : root?.repricing),
  }
  // Legacy only: a single summary rule with no dest identity fills the first row.
  if (!anyMatched && rawRules.length <= 1 && fallbackRule.repricing) {
    matched[0] = applyRuleToRow(defaults[0], fallbackRule)
  }

  return { rules: matched, repricing, adjustmentType }
}
